import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import Dot from 'svg/Dot';
import MinusBlack from 'svg/MinusBlack';
import PlusBlack from 'svg/PlusBlack';
import InputField from 'components/tournaments/InputField';
import { InputType } from 'components/tournaments/InputField/InputField';
import classNames from 'styles/utils/classNames';
import ButtonToggler from './ButtonToggler';
import { ParticipantDataType, QuickCreateFormTypes, participantsAndGamesSchema } from '../types';

const ParticipantsAndGames: React.FC<QuickCreateFormTypes> = ({
  formData,
  nextStep,
  prevStep,
  handleSubmitData,
}) => {
  const events = formData?.events?.events ?? [];

  const defaultValues: ParticipantDataType = {
    participantsAndGames: events.map((event, index) => ({
      eventId: formData?.participantsAndGames?.[index]?.eventId || event.eventId,
      maxTeams: formData?.participantsAndGames?.[index]?.maxTeams || 1,
      minGames: formData?.participantsAndGames?.[index]?.minGames || 1,
      eventTitle: event?.activeTab === 0 ? event.doubles : event.singles,
      eventTab: event?.activeTab === 0 ? 'Doubles' : 'Singles',
    })),
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ParticipantDataType>({
    resolver: zodResolver(participantsAndGamesSchema),
    defaultValues,
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'participantsAndGames',
  });

  const onSubmit: SubmitHandler<ParticipantDataType> = (data) => {
    if (nextStep) {
      if (formData?.participantsAndGames) {
        handleSubmitData &&
          handleSubmitData({ ...formData, participantsAndGames: data.participantsAndGames });
      }
    }
  };

  const maxIndex = events.length - 1;

  return (
    <>
      <div className="mx-auto w-full max-w-[23.5rem]">
        <h5 className="typography-product-heading mb-6 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Participants & Games
        </h5>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          {fields.map((event, index) => (
            <div
              key={event.id}
              className={classNames(
                'flex flex-col gap-6 pb-6',
                index < maxIndex
                  ? 'border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode'
                  : '',
              )}
            >
              <p className="typography-product-subheading flex items-center gap-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {event.eventTitle}
                <Dot className="h-1 w-1 text-color-text-brand dark:text-color-text-brand" />
                {event.eventTab}
              </p>

              {/* Maximum Teams */}
              <div>
                <label className="typography-product-body-highlight mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Maximum number of teams per event?
                </label>
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className={classNames(
                      'flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary px-6 dark:bg-color-bg-darkmode-secondary',
                      event?.maxTeams === 1 ? 'opacity-50' : '',
                    )}
                    onClick={() => update(index, { ...event, maxTeams: event.maxTeams - 1 })}
                    disabled={event?.maxTeams === 1}
                  >
                    <MinusBlack className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                  </button>
                  <Controller
                    name={`participantsAndGames.${index}.maxTeams`}
                    control={control}
                    render={({ field }) => (
                      <InputField
                        fieldLabel="Title"
                        errors={errors}
                        placeholder=""
                        name={`participantsAndGames.${index}.maxTeams`}
                        register={register}
                        inputProps={{
                          type: 'number',
                          onWheel: (e) => {
                            e.preventDefault();
                            e.currentTarget.blur();
                          },
                          min: 1,
                        }}
                        inputType={InputType.RegularWithoutLabel}
                        addStyles="text-center"
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary px-6 dark:bg-color-bg-darkmode-secondary"
                    onClick={() => update(index, { ...event, maxTeams: event.maxTeams + 1 })}
                  >
                    <PlusBlack className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                  </button>
                </div>
                {errors?.participantsAndGames?.[index]?.maxTeams && (
                  <p className="mt-2 text-sm text-color-text-brand dark:text-color-text-brand">
                    {errors.participantsAndGames[index]?.maxTeams?.message}
                  </p>
                )}
              </div>

              {/* Minimum Games */}
              <div>
                <label className="typography-product-body-highlight mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  What is the minimum number of games?
                </label>
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className={classNames(
                      'flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary px-6 dark:bg-color-bg-darkmode-secondary',
                      event?.minGames === 1 ? 'opacity-50' : '',
                    )}
                    onClick={() => update(index, { ...event, minGames: event.minGames - 1 })}
                    disabled={event?.minGames === 1}
                  >
                    <MinusBlack className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                  </button>
                  <Controller
                    name={`participantsAndGames.${index}.minGames`}
                    control={control}
                    render={({ field }) => (
                      <InputField
                        fieldLabel="Title"
                        errors={errors}
                        placeholder=""
                        name={`participantsAndGames.${index}.minGames`}
                        register={register}
                        inputProps={{
                          type: 'number',
                          onWheel: (e) => {
                            e.preventDefault();
                            e.currentTarget.blur();
                          },
                          min: 1,
                        }}
                        inputType={InputType.RegularWithoutLabel}
                        addStyles="text-center"
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary px-6 dark:bg-color-bg-darkmode-secondary"
                    onClick={() => update(index, { ...event, minGames: event.minGames + 1 })}
                  >
                    <PlusBlack className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                  </button>
                </div>
                {errors?.participantsAndGames?.[index]?.minGames && (
                  <p className="mt-2 text-sm text-color-text-lightmode-error dark:text-color-text-darkmode-error">
                    {errors.participantsAndGames[index]?.minGames?.message}
                  </p>
                )}
              </div>
            </div>
          ))}
        </form>
      </div>

      <ButtonToggler
        type="button"
        isLoading={false}
        prevStep={prevStep}
        nextStep={() => {
          if (!isValid) {
            handleSubmit(onSubmit)();
          } else if (formData && nextStep) {
            handleSubmit(onSubmit)();
          }
        }}
        nextTitle="Next"
      />
    </>
  );
};

export default ParticipantsAndGames;
